import React, { useMemo } from 'react'
import { Row, Col } from 'reactstrap'

import ActivityHeatmap from '../charts/ActivityHeatmap'
import ChartCard from '../charts/ChartCard'
import DataTable from '../charts/DataTable'
import Leaderboard from '../charts/Leaderboard'
import StatTile, { StatTileRow } from '../charts/StatTile'
import TrendLine from '../charts/TrendLine'
import {
    buildHeatmap,
    buildKpis,
    buildLeaderboard,
    buildLeaderboardTable,
    buildTrend,
    countUniqueUpdatedObjects,
} from '../activityReport'

const UsersTab = ({ rows, updateActionsSelected = true, startDate, endDate, activeView }) => {
    const { leaderboard, heatmap, trend, kpis } = useMemo(
        () => ({
            leaderboard: buildLeaderboard(rows),
            heatmap: buildHeatmap(rows, startDate, endDate),
            trend: buildTrend(rows, startDate, endDate),
            kpis: buildKpis(rows, startDate, endDate),
        }),
        [rows, startDate, endDate]
    )

    // Counted from the same rows the charts use -- the ids ride along on them, so
    // there is no second fetch to be out of step with.
    const uniqueUpdated = useMemo(() => countUniqueUpdatedObjects(rows), [rows])

    const leaderboardTable = buildLeaderboardTable(leaderboard.data, leaderboard.series)

    return (
        <>
            <StatTileRow>
                <StatTile
                    label="Total actions"
                    value={kpis.totalActions}
                    caption="Across selected action types"
                    spark={kpis.totalSpark}
                    tone="created"
                    accent="created"
                />
                <StatTile label="Active users" value={kpis.activeUsers} caption="People with recorded activity" />
                <StatTile
                    label="Busiest day"
                    value={kpis.busiestDay}
                    caption={`${kpis.busiestDayCount.toLocaleString()} actions`}
                />
                <StatTile
                    label="Top contributor"
                    value={kpis.topUser}
                    caption={`${kpis.topUserCount.toLocaleString()} actions`}
                />
                {/*
                    '--' rather than 0 when no update action is selected: there is
                    genuinely no unique-updated figure to report then, and a hard
                    zero reads as "nobody updated anything".
                */}
                <StatTile
                    label="Unique records updated"
                    value={updateActionsSelected ? uniqueUpdated : '--'}
                    caption={updateActionsSelected ? 'Distinct records edited' : 'No update actions selected'}
                />
            </StatTileRow>

            <Row>
                <Col xs="12">
                    <ChartCard
                        title="Effort by User"
                        subtitle="Two bars per person — created above, updated below. Creations count distinct records; updates count edit events, so several can land on one record."
                        isEmpty={!leaderboard.data.length}
                    >
                        {activeView === 'table' ? (
                            <DataTable rows={leaderboardTable.rows} series={leaderboardTable.columns} />
                        ) : (
                            <Leaderboard data={leaderboard.data} series={leaderboard.series} />
                        )}
                    </ChartCard>
                </Col>
            </Row>

            <Row>
                <Col xs="12">
                    <ChartCard
                        title="Activity Over Time"
                        subtitle="Daily totals by record type"
                        isEmpty={!trend.series.length}
                    >
                        <TrendLine data={trend.data} series={trend.series} />
                    </ChartCard>
                </Col>
            </Row>

            <Row>
                <Col xs="12">
                    <ChartCard
                        title="Daily Intensity"
                        subtitle="Darker means a heavier day — hover a cell for the count"
                        isEmpty={!heatmap.grid.length}
                    >
                        <ActivityHeatmap days={heatmap.days} grid={heatmap.grid} peak={heatmap.peak} />
                    </ChartCard>
                </Col>
            </Row>
        </>
    )
}

export default UsersTab
